
import { createContext, useContext, useEffect, useRef, useState } from "react";
import MainContext from "./MainContext";

export const WebSocketContext = createContext(null);
export const useWebSocket = () => useContext(WebSocketContext);


export const WebSocketProvider = ({ user, children }) => {
    const context = useContext(MainContext);
    const [websocket, setWebsocket] = useState(null);
    const websocketRef = useRef(null);
    const [websocketPingInterval, setWebsocketPingInterval] = useState(null);

    // Logica di handleReceivedMessage come in App.js
    const handleReceivedMessage = (data) => {
        if (data.type === "messaging.unread") {
            context.setUnreadMessages(data.value)
        }
        if (data.type === "messaging.message" && data.profile_role_id !== context.user?.profile_role_id) {
            context.setUnreadMessages((prev) => prev + 1)
            if (!window.location.pathname.includes("messages")) {
                context.setNotificationBanner({ title: `${data.name} ${data.surname}`, body: data.body?.text ?? '', url: "/messages", image: data.picture })
            }
        }
        document.dispatchEvent(new CustomEvent('message-received', { detail: data }));
    };

    useEffect(() => {
        // Cleanup sincrono PRIMA di creare una nuova connessione
        if (websocketRef.current) {
            clearInterval(websocketPingInterval);
            setWebsocket(null);
            websocketRef.current.close();
            websocketRef.current = null;
        }
        let cancelled = false;
        if (user && !user.isAnonymous) {
            import('../websocket').then(({ default: WebSocketClient }) => {
                if (cancelled || websocketRef.current) return;
                const wsClient = new WebSocketClient(handleReceivedMessage);
                wsClient.init();
                websocketRef.current = wsClient;
                setWebsocket(wsClient);
                const wsPingInterval = setInterval(() => {
                    wsClient.sendMessage({ type: "messaging.ping" });
                }, 5000);
                setWebsocketPingInterval(wsPingInterval);
            }).catch((error) => {
                console.error('Errore durante il caricamento del WebSocket client', error);
            });
        }
        return () => {
            cancelled = true;
            if (websocketRef.current) {
                clearInterval(websocketPingInterval);
                setWebsocket(null);
                websocketRef.current.close();
                websocketRef.current = null;
            }
        };
    }, [user]);

    useEffect(() => {
        const sendPageView = () => {
            if (!websocketRef.current) {
                return;
            }

            websocketRef.current.sendMessage({
                type: "analytics.page_view",
                page: window.location.pathname,
                title: document.title || "Dashboard",
            });
        };

        const onNavigate = () => sendPageView();

        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function (...args) {
            const result = originalPushState.apply(this, args);
            window.dispatchEvent(new Event("app:navigation"));
            return result;
        };

        window.history.replaceState = function (...args) {
            const result = originalReplaceState.apply(this, args);
            window.dispatchEvent(new Event("app:navigation"));
            return result;
        };

        window.addEventListener("app:navigation", onNavigate);
        window.addEventListener("popstate", onNavigate);
        document.addEventListener("websocket-connected", onNavigate);

        if (user && !user.isAnonymous) {
            sendPageView();
        }

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener("app:navigation", onNavigate);
            window.removeEventListener("popstate", onNavigate);
            document.removeEventListener("websocket-connected", onNavigate);
        };
    }, [user]);

    return (
        <WebSocketContext.Provider value={{ websocket }}>
            {children}
        </WebSocketContext.Provider>
    );
};